"use client";

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useComponentTranslations } from '@/app/components/LanguageProvider';
import { Icon } from '@iconify/react';
import NextImage from 'next/image';
import { IndustrialsForm } from '@/app/components/IndustrialsForm';

type SupportClientProps = {
    initialTranslations: Record<string, string>;
    initialCommonTranslations: Record<string, string>;
};

const fadeIn = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
};

const SupportClient = ({
    initialTranslations,
    initialCommonTranslations,
}: SupportClientProps) => {
    const { t, translationsLoaded } = useComponentTranslations('Support');
    const [openFaq, setOpenFaq] = useState<number | null>(null);

    const et = (key: string, fallback?: string) => {
        const initial = initialTranslations[key] ?? initialCommonTranslations[key];
        if (!translationsLoaded) return initial ?? fallback ?? key;
        return t(key, fallback) ?? initial ?? fallback ?? key;
    };

    const services = [
        { 
            title: et('supportServiceMonitoringTitle'), 
            description: et('supportServiceMonitoringDesc'), 
            detail: et('supportServiceMonitoringDetail'),
            icon: 'carbon:monitor',
            image: '/images/csd.jpg' 
        },
        { 
            title: et('supportServiceMaintenanceTitle'), 
            description: et('supportServiceMaintenanceDesc'), 
            detail: et('supportServiceMaintenanceDetail'),
            icon: 'carbon:settings',
            image: '/images/robot-working-with-computer_1048-4282.jpg' 
        },
        { 
            title: et('supportServiceResponseTitle'), 
            description: et('supportServiceResponseDesc'), 
            detail: et('supportServiceResponseDetail'),
            icon: 'carbon:emergency',
            image: '/images/support.png' 
        },
        { 
            title: et('supportServiceEvolutionTitle'), 
            description: et('supportServiceEvolutionDesc'), 
            detail: et('supportServiceEvolutionDetail'),
            icon: 'carbon:trend-up',
            image: '/images/development.png' 
        },
    ];

    const cases = [
        { title: et('supportCase1Title'), result: et('supportCase1Result'), desc: et('supportCase1Desc'), image: '/images/retail.jpg' },
        { title: et('supportCase2Title'), result: et('supportCase2Result'), desc: et('supportCase2Desc'), image: '/images/cloud.jpg' },
        { title: et('supportCase3Title'), result: et('supportCase3Result'), desc: et('supportCase3Desc'), image: '/images/data.jpg' },
    ];

    const pricingPlans = [
        {
            name: et('supportPrice1Name'),
            price: et('supportPrice1Price'),
            desc: et('supportPrice1Desc'),
            bullets: [et('supportPrice1Bullet1'), et('supportPrice1Bullet2'), et('supportPrice1Bullet3'), et('supportPrice1Bullet4')],
            icon: 'carbon:partly-cloudy',
            popular: false,
        },
        {
            name: et('supportPrice2Name'),
            price: et('supportPrice2Price'),
            desc: et('supportPrice2Desc'),
            bullets: [et('supportPrice2Bullet1'), et('supportPrice2Bullet2'), et('supportPrice2Bullet3'), et('supportPrice2Bullet4')],
            icon: 'carbon:sun',
            popular: true,
        },
        {
            name: et('supportPrice3Name'),
            price: et('supportPrice3Price'),
            desc: et('supportPrice3Desc'),
            bullets: [et('supportPrice3Bullet1'), et('supportPrice3Bullet2'), et('supportPrice3Bullet3'), et('supportPrice3Bullet4')],
            icon: 'carbon:flash',
            popular: false,
        },
    ];

    const processSteps = [
        { step: et('supportStep1'), title: et('supportStep1Title'), desc: et('supportStep1Desc') },
        { step: et('supportStep2'), title: et('supportStep2Title'), desc: et('supportStep2Desc') },
        { step: et('supportStep3'), title: et('supportStep3Title'), desc: et('supportStep3Desc') },
        { step: et('supportStep4'), title: et('supportStep4Title'), desc: et('supportStep4Desc') },
    ];

    const faqItems = [
        { question: et('supportFaq1q'), answer: et('supportFaq1a') },
        { question: et('supportFaq2q'), answer: et('supportFaq2a') },
        { question: et('supportFaq3q'), answer: et('supportFaq3a') },
        { question: et('supportFaq4q'), answer: et('supportFaq4a') },
        { question: et('supportFaq5q'), answer: et('supportFaq5a') },
    ];

    return (
        <div className="relative min-h-screen bg-transparent text-white">
            <header className="flex flex-col justify-center items-center text-center px-4 sm:px-6 lg:px-8 pt-32 pb-16">
                <motion.h1
                    className="text-3xl lg:text-7xl font-bold tracking-tight mb-6 bg-linear-to-b from-white to-white/90 bg-clip-text text-transparent"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {et('supportPageTitle')}
                </motion.h1>
                <motion.p
                    className="text-lg text-white/60 max-w-2xl mx-auto mb-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                >
                    {et('supportPageSubtitle')}
                </motion.p>
                <motion.p
                    className="text-sm text-white/50 max-w-3xl mx-auto"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    {et('supportIntro')}
                </motion.p>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <section className="my-24">
                    <motion.h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white/95 text-center mb-16" {...fadeIn}>
                        {et('supportServicesTitle')}
                    </motion.h2>
                    <div className="space-y-16">
                        {services.map((service, index) => (
                            <motion.div
                                key={index}
                                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.5 }}
                            >
                                <div className="lg:w-1/2">
                                    <div className="glass-card glass-rounded overflow-hidden aspect-video relative">
                                        <NextImage 
                                            src={service.image} 
                                            alt={service.title} 
                                            fill
                                            className="object-cover" 
                                        />
                                    </div>
                                </div>
                                <div className="lg:w-1/2 space-y-4">
                                    <div className="flex items-center gap-3 mb-2">
                                        <Icon icon={service.icon} className="text-3xl text-blue-400" />
                                        <h3 className="text-2xl font-semibold text-white">{service.title}</h3>
                                    </div>
                                    <p className="text-white/70">{service.description}</p>
                                    <p className="text-white/50 text-sm">{service.detail}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </section>
            </div>

            <section className="w-full py-24 glass-section glass-border-y my-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white/95 text-center mb-16" {...fadeIn}>
                        {et('supportCasesTitle')}
                    </motion.h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {cases.map((item, index) => (
                            <motion.div
                                key={index}
                                className="glass-card glass-rounded overflow-hidden glass-hover glass-transition"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.4, delay: index * 0.1 }}
                            >
                                <div className="relative h-48">
                                    <NextImage src={item.image} alt={item.title} fill className="object-cover" />
                                    <div className="absolute inset-0 bg-linear-to-t from-black/80 to-transparent" />
                                    <div className="absolute bottom-4 left-4 right-4">
                                        <div className="text-blue-400 text-sm font-semibold mb-1">{item.result}</div>
                                        <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <p className="text-sm text-white/70 leading-normal">{item.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <section className="my-24">
                    <motion.div className="text-center mb-16" {...fadeIn}>
                        <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white/95 mb-4">{et('supportPricingTitle')}</h2>
                        <p className="text-white/70 max-w-xl mx-auto">{et('supportPricingSubtitle')}</p>
                    </motion.div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {pricingPlans.map((plan, index) => (
                            <motion.div
                                key={index}
                                className={`glass-card glass-rounded p-8 glass-hover glass-transition relative ${plan.popular ? 'border-blue-400/50 border-2' : ''}`}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.4, delay: index * 0.1 }}
                            >
                                {plan.popular && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-xs font-semibold px-4 py-1 rounded-full">
                                        Popular
                                    </div>
                                )}
                                <Icon icon={plan.icon} className="text-4xl text-blue-400 mb-4" />
                                <h3 className="text-xl font-semibold text-white mb-2">{plan.name}</h3>
                                <div className="text-2xl font-bold text-blue-400 mb-4">{plan.price}</div>
                                <p className="text-sm text-white/70 mb-6">{plan.desc}</p>
                                <ul className="space-y-3">
                                    {plan.bullets.map((bullet, i) => (
                                        <li key={i} className="flex items-start gap-2 text-sm text-white/70">
                                            <Icon icon="carbon:checkmark" className="text-blue-400 mt-0.5 shrink-0" />
                                            {bullet}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>
                </section>

                <section className="my-24">
                    <motion.h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white/95 text-center mb-16" {...fadeIn}>
                        {et('supportProcessTitle')}
                    </motion.h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {processSteps.map((step, index) => (
                            <motion.div
                                key={index}
                                className="p-8 glass-border-strong glass-rounded glass-card"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.4, delay: index * 0.1 }}
                            >
                                <div className="text-5xl font-bold text-blue-400/30 mb-4">{step.step}</div>
                                <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                                <p className="text-white/70 leading-normal text-sm">{step.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </section>

                <section className="my-24 max-w-3xl mx-auto">
                    <motion.h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white/95 text-center mb-12" {...fadeIn}>
                        {et('supportFaqTitle')}
                    </motion.h2>
                    <div className="space-y-4">
                        {faqItems.map((item, index) => (
                            <motion.div
                                key={index}
                                className="glass-card glass-rounded overflow-hidden"
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.3, delay: index * 0.05 }}
                            >
                                <button
                                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                                    className="w-full p-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                                >
                                    <span className="font-medium pr-4">{item.question}</span>
                                    <Icon 
                                        icon={openFaq === index ? 'carbon:chevron-up' : 'carbon:chevron-down'} 
                                        className="text-xl text-blue-400 shrink-0" 
                                    />
                                </button>
                                {openFaq === index && (
                                    <div className="px-5 pb-5 text-white/60 text-sm leading-relaxed">
                                        {item.answer}
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </section>

                <IndustrialsForm label={et('supportCtaTitle')} subtitle={et('supportCtaSubtitle')} />
            </div>
        </div>
    );
};

export default SupportClient;
